import React, { useEffect, useState } from "react";
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import {
  portalGetBalances,
  portalGetMe,
  portalListTransactions,
} from "../../services/merchant-portal";
import "./portal.scss";

const MerchantPortalDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [balances, setBalances] = useState([]);
  const [walletEur, setWalletEur] = useState(null);
  const [recentTx, setRecentTx] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [meRes, balRes, txRes] = await Promise.all([
          portalGetMe(),
          portalGetBalances(),
          portalListTransactions({ page: 1, size: 5 }),
        ]);
        if (cancelled) return;

        if (meRes.status_code === 200) {
          setProfile(meRes.data);
        } else {
          toast.error(meRes.message || "Could not load profile");
        }

        if (balRes.status_code === 200) {
          setBalances(balRes.data?.balances || []);
          setWalletEur(
            balRes.data?.wallet_balance_eur ??
              meRes.data?.wallet?.balance_eur ??
              null,
          );
        } else {
          toast.error(balRes.message || "Could not load balances");
        }

        if (txRes.status_code === 200) {
          setRecentTx(txRes.data?.items || []);
        }
      } catch (err) {
        if (!cancelled) toast.error(`${err}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <BeatLoader color="#03C9D7" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-1">
        Welcome{profile?.name ? `, ${profile.name}` : ""}
      </h1>
      <p className="portal-muted mb-6">
        {profile?.kyb_status
          ? `KYB status: ${profile.kyb_status}`
          : "Manage balances and recent activity."}
      </p>

      <section className="portal-card mb-6">
        <h2 className="portal-section-title">Ledger balances</h2>
        {walletEur != null ? (
          <p className="portal-muted mb-3">
            EUR wallet (settlement): <strong>{walletEur}</strong>
          </p>
        ) : null}
        {balances.length === 0 ? (
          <p className="portal-muted">No ledger balances yet.</p>
        ) : (
          <div className="portal-grid">
            {balances.map((b) => (
              <div key={b.code || b.currency} className="portal-balance-tile">
                <div className="text-sm opacity-75">{b.code || b.currency}</div>
                <div className="portal-balance-amount">{b.amount}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="portal-card">
        <h2 className="portal-section-title">Recent transactions</h2>
        {recentTx.length === 0 ? (
          <p className="portal-muted">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left opacity-75 border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">Reference</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Amount</th>
                  <th className="py-2 pr-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentTx.map((tx) => (
                  <tr
                    key={tx.external_reference_id}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-2 pr-3 font-mono text-xs">
                      {tx.external_reference_id}
                    </td>
                    <td className="py-2 pr-3">{tx.transaction_type}</td>
                    <td className="py-2 pr-3">
                      {tx.amount} {tx.currency}
                    </td>
                    <td className="py-2 pr-3">{tx.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default MerchantPortalDashboard;
